import type { PublicKey } from '@shared/types';
import { deriveIdentityIdFromPublicKey } from '@shared/identityId';
import { t } from '../i18n';
import { useVaultStore } from '../store/vaultStore';
import { showToast } from '../utils/toast';
import { requestCallHistorySync } from '../services/callHistorySync';
import { setPendingCallRemoteKey } from '../store/callHistoryStore';

export function getPinnedPublicKeyForIdentity(identityId: string): PublicKey | null {
  const cleanIdentityId = (identityId || '').trim();
  if (!cleanIdentityId) return null;

  const contact = useVaultStore.getState().findContactByIdentityId(cleanIdentityId);
  const identity = contact?.identities.find((item) => (item.identityId || '').trim() === cleanIdentityId) || null;
  return identity?.identityPublicKey || null;
}

export function isSignalingKeyConsistentWithPinnedKey(params: {
  signalingKey?: PublicKey | null;
  pinnedKey?: PublicKey | null;
}): boolean {
  const signaling = (params.signalingKey?.value || '').trim();
  const pinned = (params.pinnedKey?.value || '').trim();
  if (!signaling || !pinned) return true;
  return signaling === pinned;
}

export function showSoftKeyMismatchToast() {
  showToast({
    message: t('call.security.keyMismatchSoft'),
    variant: 'warning',
    durationMs: 7000
  });
}

export async function deriveSafeIdentityId(publicKey?: PublicKey | null): Promise<string> {
  if (!publicKey?.value) return '';
  try {
    return (await deriveIdentityIdFromPublicKey(publicKey)).trim();
  } catch {
    return '';
  }
}

export function addVerifiedIncomingCallHistory(params: {
  callSessionId: string;
  remoteIdentityId: string;
  remoteDisplayName: string;
  remoteIdentityName?: string;
  remotePublicKey?: PublicKey;
  isKnownContact: boolean;
  isTemporaryLinkCall: boolean;
  localIdentityId?: string;
  localServerId?: string;
  localServerName?: string;
  localServerUrl?: string;
}) {
  if (params.callSessionId && params.remotePublicKey?.value) {
    setPendingCallRemoteKey(params.callSessionId, params.remotePublicKey);
  }
}

export function updateHistoryRemoteIdentityName(callSessionId: string, remoteIdentityName: string) {
  void callSessionId;
  void remoteIdentityName;
  void requestCallHistorySync().catch(() => {});
}

export function updateHistoryRemoteIdentityId(callSessionId: string, remoteIdentityId: string) {
  void callSessionId;
  void remoteIdentityId;
  void requestCallHistorySync().catch(() => {});
}

export function markOutgoingAnsweredVerified(params: {
  callSessionId: string;
  remotePublicKey?: PublicKey;
  remoteIdentityName?: string;
}) {
  if (params.callSessionId && params.remotePublicKey?.value) {
    setPendingCallRemoteKey(params.callSessionId, params.remotePublicKey);
  }
  void requestCallHistorySync().catch(() => {});
}

export function markIncomingRejected(callSessionId: string) {
  void callSessionId;
  void requestCallHistorySync().catch(() => {});
}
